// src/templates/search/overlay/search-overlay.js

/**
 * Hoffmaster Search Overlay Module
 *
 * Handles:
 * - Opening/closing search overlay
 * - Form submission
 * - Input validation (minimum 3 characters)
 * - Body scroll prevention
 * - Keyboard navigation (ESC to close)
 */

class HoffmasterSearchOverlay {
	constructor() {
		this.overlay = null;
		this.input = null;
		this.form = null;
		this.closeButton = null;
		this.errorMessage = null;
		this.triggerButtons = [];

		this.init();
	}

	init() {
		// Wait for DOM to be ready
		if (document.readyState === 'loading') {
			document.addEventListener('DOMContentLoaded', () => this.setup());
		} else {
			this.setup();
		}
	}

	setup() {
		// Get overlay elements
		this.overlay = document.querySelector('.hm-search-overlay');
		if (!this.overlay) return;

		this.input = this.overlay.querySelector('.hm-search-overlay__input');
		this.form = this.overlay.querySelector('.hm-search-overlay__form');
		this.closeButton = this.overlay.querySelector('.hm-search-overlay__close');
		this.errorMessage = this.overlay.querySelector('.hm-search-overlay__error');

		// Get trigger buttons (magnifier icons in header)
		this.triggerButtons = document.querySelectorAll('.hm-header__actions--search--link');

		this.bindEvents();
	}

	bindEvents() {
		// Open overlay when clicking magnifier icon
		this.triggerButtons.forEach((button) => {
			button.addEventListener('click', (e) => {
				e.preventDefault();
				this.open();
			});
		});

		// Close overlay when clicking close button
		if (this.closeButton) {
			this.closeButton.addEventListener('click', () => this.close());
		}

		// Close overlay when clicking backdrop
		if (this.overlay) {
			this.overlay.addEventListener('click', (e) => {
				if (e.target === this.overlay) {
					this.close();
				}
			});
		}

		// Close overlay on ESC key
		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape' && this.isOpen()) {
				this.close();
			}
		});

		// Handle form submission
		if (this.form) {
			this.form.addEventListener('submit', (e) => {
				e.preventDefault();
				this.handleSubmit();
			});
		}

		// Clear error on input
		if (this.input) {
			this.input.addEventListener('input', () => {
				this.hideError();
			});
		}
	}

	open() {
		if (!this.overlay) return;

		this.overlay.classList.add('hm-search-overlay--active');
		document.body.classList.add('hm-search-overlay-open');

		// Focus input after animation
		setTimeout(() => {
			if (this.input) {
				this.input.focus();
			}
		}, 300);
	}

	close() {
		if (!this.overlay) return;

		this.overlay.classList.remove('hm-search-overlay--active');
		document.body.classList.remove('hm-search-overlay-open');

		// Clear input and error
		if (this.input) {
			this.input.value = '';
		}
		this.hideError();
	}

	isOpen() {
		return this.overlay && this.overlay.classList.contains('hm-search-overlay--active');
	}

	handleSubmit() {
		const query = this.input ? this.input.value.trim() : '';

		// Validate minimum 3 characters
		if (query.length < 3) {
			this.showError('Please enter at least 3 characters to search.');
			return;
		}

		// Get home URL from localized script (handles subdirectory installs)
		// Falls back to window.location.origin for environments without localization
		const homeUrl = window.hoffmasterSearchOverlay?.homeUrl || window.location.origin;

		// Remove trailing slash from homeUrl to avoid double slashes
		const baseUrl = homeUrl.replace(/\/$/, '');

		// Redirect to search results page
		const searchUrl = `${baseUrl}/?s=${encodeURIComponent(query)}`;
		window.location.href = searchUrl;
	}

	showError(message) {
		if (this.errorMessage) {
			this.errorMessage.textContent = message;
			this.errorMessage.classList.add('hm-search-overlay__error--visible');
		}
	}

	hideError() {
		if (this.errorMessage) {
			this.errorMessage.classList.remove('hm-search-overlay__error--visible');
		}
	}
}

// Initialize search overlay
new HoffmasterSearchOverlay();
